 
import React from "react";
import { Button, Link } from "@mui/material";
import { useOutletContext } from "react-router-dom";


import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { LoadState } from "../../../../../../constants/enums";
import { useDispatchWrapper,
  useBreadcrumb,
} from "../../../../../../hooks";
import { useCommAutoScope } from "../../../../../leads/ManageLead/LeadCommAutoScopeContext";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { ICommAutomobileGaragePlates } from "../../../../../../redux/commAutoPolicy/commAutoGaragePlates/commAutoGaragePlates.types";
import {
  clearCommAutomobileGaragePlatesState,
  fetchCommAutomobileGaragePlatesListAsync,
} from "../../../../../../redux/commAutoPolicy/commAutoGaragePlates/commAutoGaragePlatesActions";
import { CommAutoGaragePlatesDialog } from "./CommAutoGaragePlateDialog";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import { AddOutlined } from "@mui/icons-material";
import { PageContainer } from "../../../../../../components/container/PageContainer";

const INITIAL_GARAGE_PLATE: ICommAutomobileGaragePlates = {
  customer_policy_id: null,
  serial_number: null,
  type: null,
  plate_number: null,
  charge_details: null,
  assigned_to: null,
  attachment: null,
  status: "ACTIVE",
};

export const CommAutoGaragePlatesList: React.FC<{
  /**
   * Optional lead-scoped identifier. When present (either via this prop or the
   * lead Comm Auto scope provider) the list fetches and the dialog persists
   * keyed by `lead_id`; otherwise the customer-policy path is unchanged.
   */
  leadId?: string;
}> = (props) => {
  // In lead scope this component is rendered outside a router `<Outlet>`, so
  // `useOutletContext()` returns undefined. Read it defensively and resolve the
  // operating scope through `useCommAutoScope`, which prefers the lead scope
  // (keyed by `lead_id`) and otherwise returns the route fallback verbatim,
  // preserving the customer-policy behavior byte-for-byte.
  const outlet = useOutletContext() as [number?, number?] | undefined;
  const scope = useCommAutoScope({
    customerId: outlet?.[0],
    customerPolicyId: outlet?.[1],
  });

  const leadId = props.leadId ?? scope.leadId;
  const customerId = scope.customerId;
  const customerpolicyId = scope.customerPolicyId;

  useBreadcrumb({
    title: "",
    items: [
      {
        to: "/dashboard",
        title: "dashboard",
      },
      {
        to: "/view-customers",
        title: "customers",
      },
      {
        to: "/customer-overview/" + customerId,
        title: "customer-overview",
      },
      {
        to:
          "/comm-automobile-policy/comm-automobile/" +
          customerId +
          "/" +
          customerpolicyId,
        title: "comm-auto",
      },
      {
        title: "garage-plate",
      },
    ],
  });

  React.useEffect(() => {
    return () => {
      dispatch(clearCommAutomobileGaragePlatesState());
    };
  }, []);

  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.garagePlate
  );

  const dispatch = useDispatchWrapper();
  const [openGaragePlate, setOpenGaragePlate] =
    React.useState<ICommAutomobileGaragePlates | null>(null);

  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE"
  );

  const {
    state: { dateState, searchState, tabs, columnsConfig,
      dense },
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
  });

  // In lead scope the list is fetched keyed by `lead_id` (customer_policy_id is
  // undefined); in customer-policy scope the original fetch runs unchanged.
  const fetchList = () => {
    if (leadId) {
      dispatch(
        fetchCommAutomobileGaragePlatesListAsync(
          customerpolicyId as number,
          status,
          leadId
        )
      );
    } else if (customerpolicyId) {
      dispatch(
        fetchCommAutomobileGaragePlatesListAsync(customerpolicyId, status)
      );
    }
  };

  React.useEffect(() => {
    fetchList();

    return () => {
      dispatch(clearCommAutomobileGaragePlatesState());
    };
  }, [customerpolicyId, leadId, status]);

  const handleCloseGaragePlate = () => {
    setOpenGaragePlate(null);

  };
  const handleGaragePlateComplete = () => {
    if (leadId) {
      dispatch(
        fetchCommAutomobileGaragePlatesListAsync(
          customerpolicyId as number,
          undefined,
          leadId
        )
      );
    } else {
      dispatch(
        fetchCommAutomobileGaragePlatesListAsync(customerpolicyId as number)
      );
    }
    handleCloseGaragePlate();
  };

  const garagePlateTableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS}
              />
            ),
          },
          // {
          //   key: "status",
          //   onRender: () => (
          //     <Button
          //       variant="contained"
                // onClick={() => {
                //   setOpenGaragePlate({
                //     ...INITIAL_GARAGE_PLATE,
                //     customer_policy_id: customerpolicyId,
                //   });
                // }}
          //     >
          //       Add Garage Plate
          //     </Button>
          //   ),
          // },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileGaragePlates) => {
          return (
            <StandardTableActions onEditClick={() => setOpenGaragePlate(row)} />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileGaragePlates) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "serial_number",
        headerName: "#",
        fieldName: "serial_number",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 15,
        onRowCellRender: (value, row: ICommAutomobileGaragePlates) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => setOpenGaragePlate(row)}
            >
              {row.serial_number}
            </Link>
          );
        },
      },
      {
        key: "plate_number",
        headerName: "Plate Number",
        fieldName: "plate_number",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 20,
        onRowCellRender: (value, row: ICommAutomobileGaragePlates) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => setOpenGaragePlate(row)}
            >
              {row.plate_number}
            </Link>
          );
        },
      },
      {
        key: "type",
        headerName: "Type",
        fieldName: "type",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
        onRowCellRender: (value, row: ICommAutomobileGaragePlates) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => setOpenGaragePlate(row)}
            >
              {row.type}
            </Link>
          );
        },
      },
    ],
    rows: data,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  return (
    <PageContainer title="Garage Plates" description="this is innerpage">
      <PageTitleBar
        heading="Garage Plates"
        rightHeading={
          <Button
            variant="contained"
            onClick={() => {
              setOpenGaragePlate({
                ...INITIAL_GARAGE_PLATE,
                customer_policy_id: customerpolicyId ?? null,
              });
            }}
            startIcon={<AddOutlined />}
          >
            Create
          </Button>
        }
      />
      <DataTableV2 {...garagePlateTableProps} />
      {openGaragePlate && (
        <CommAutoGaragePlatesDialog
          open={true}
          data={openGaragePlate}
          leadId={leadId}
          customerId={customerId}
          onClose={handleCloseGaragePlate}
          onComplete={handleGaragePlateComplete}
        />
      )}
    </PageContainer>
  );
};
